import { AfterViewInit, Component, OnInit } from '@angular/core';
import { CustomDashboardsService } from 'app/modules/my-limegear/services/my-limegear.service';
import {
    ECloseService,
    IECloseCardInfo,
    IECloseData,
} from 'app/modules/production/services/eClose.service';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from 'app/modules/shared/interfaces/no-data';
import { Router } from '@angular/router';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { ICardControllerInfo } from 'app/modules/shared/components/card-controller-panel/card-controller-panel.component';
import { DisplayOptions } from 'app/modules/shared/interfaces/display-options';

@Component({
    selector: 'e-closing-mix-lgc',
    templateUrl: './e-closing-mix-lgc.component.html',
    styleUrls: ['./e-closing-mix-lgc.component.scss'],
})
export class EClosingMixLgcComponent implements OnInit, AfterViewInit {
    constructor(
        private _customDashboardsService: CustomDashboardsService,
        private _eCloseService: ECloseService,
        private _reusableFunctions: ReusableFunctions,
        private _router: Router
    ) {}

    chartCardInfo: IECloseCardInfo;
    cardControllerInfo: ICardControllerInfo;
    hasData: boolean;
    isDataReady: boolean;
    closingMixLegend: ColorLegendData[];
    noDataType: NoDataTypes;
    legendTextUnits: LegendData;
    legendTextVolume: LegendData;
    unitDonutChartData: AdoptionTrendsDonutChartData[];
    volumeDonutChartData: AdoptionTrendsDonutChartData[];
    closingMixData: ClosingMixDataInfo[];

    ngOnInit() {
        this.chartCardInfo = {
            title: 'Closing Mix',
            uniqueId: 'loans-funded-by',
            cardId: 'eClosingMix',
            showLegend: true,
            setRoiJson: '0',
            module: 'Production',
        };

        this.cardControllerInfo = {
            cardId: this.chartCardInfo.cardId,
            showCardControllers: true,
            showDropdown: true,
            cardTitle: this.chartCardInfo.title,
        };

        this.hasData = false;
        this.isDataReady = false;
        this.closingMixLegend = [];

        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: this.chartCardInfo.title,
            inputSource: NoDataSourceType.largeDataCard,
        };

        this._eCloseService.initializeECloseCard(this.chartCardInfo, this.noDataType.action);
    }

    ngAfterViewInit() {
        this._customDashboardsService.filterChangesSubject$.subscribe((changes) => {
            this.getChartData(
                this.chartCardInfo,
                changes.displayOptions,
                changes.hierarchyfilters,
                changes.action
            );
        });
    }

    getChartData(
        cardInfo: IECloseCardInfo,
        displayOptions: DisplayOptions[],
        hFilterJSON: string,
        action: NoDataActionType
    ) {
        let parameters = this._eCloseService.setECloseParameters(displayOptions, hFilterJSON);

        parameters.cardId = cardInfo.cardId;
        if (cardInfo.setRoiJson) {
            parameters.setRoiJson = cardInfo.setRoiJson;
        }

        this.hasData = false;
        this.isDataReady = false;
        this.noDataType.action = action;

        this._eCloseService.getECloseData(parameters).subscribe(
            (data) => {
                if (data && data.length > 0) {
                    this.prepareClosingMixData(data);
                } else {
                    this.hasData = false;
                    this.isDataReady = true;
                }
            },
            (error) => {
                this.hasData = false;
                this.isDataReady = true;
                this.noDataType.action = NoDataActionType.error;
            }
        );
    }

    prepareClosingMixData(data: IECloseData[]) {
        const aggregateDataByClosingMethod = data.reduce((acc, curr) => {
            if (!acc[curr.category]) {
                acc[curr.category] = {
                    category: curr.category,
                    amount: curr.amount,
                    nbr: curr.nbr,
                };
            } else {
                acc[curr.category].amount += +curr.amount;
                acc[curr.category].nbr += +curr.nbr;
                acc[curr.category].eCloseOptionKey = curr.eCloseOptionKey;
            }
            return acc;
        }, {});

        this.closingMixData = Object.values(aggregateDataByClosingMethod);
        let totalUnits = data.reduce((a, { nbr }) => a + nbr, 0);
        let totalVolume = data.reduce((a, { amount }) => a + amount, 0);
        this.prepareClosingMixCenterText(totalUnits, totalVolume);
        this.prepareClosingMixLegend();

        this.unitDonutChartData = this.setDonutChartData(
            this.closingMixData,
            'Closing Mix',
            'nbr',
            totalUnits,
            totalVolume
        );
        this.volumeDonutChartData = this.setDonutChartData(
            this.closingMixData,
            'Closing Mix',
            'amount',
            totalUnits,
            totalVolume
        );
        this.isDataReady = true;
        this.hasData = true;
    }

    setDonutChartData(
        data: ClosingMixDataInfo[],
        filterType: string,
        dataType: string,
        totalUnits: number,
        totalVolume: number
    ): AdoptionTrendsDonutChartData[] {
        let colors = this._reusableFunctions.getECloseColors();

        return data.map((i) => {
            return {
                name: i.category,
                color: colors[i.category],
                y: i[dataType],
                properties: {
                    units: i.nbr,
                    volume: i.amount,
                    milestone: 'all',
                    loanType: 'all',
                    filterType: filterType,
                    filterValue: i.category,
                    router: this._router,
                    eCloseOptionKey: i.eCloseOptionKey,
                    navFromUrl: '',
                    percentage: 100 * (i.nbr / totalUnits),
                    alternatePercentage: 100 * (i.amount / totalVolume),
                },
            };
        });
    }

    prepareClosingMixCenterText(totalUnits: number, totalVolume: number) {
        let textClickParams = {
            routerUrl: '/reporting/production/loans-funded-by',
            queryParams: {
                titleSuffix: `Closing Mix`,
                navFromUrl: '/production/insights/eclose-adoption-trends',
            },
        };

        this.legendTextUnits = {
            title: this._reusableFunctions.formatNumber(totalUnits).toString(),
            description: 'Funded',
            type: 'Units',
            textClickParams: textClickParams,
        };
        this.legendTextVolume = {
            title: this._reusableFunctions.getLargeCurrencyFormat(totalVolume),
            description: 'Funded',
            type: 'Volume',
            textClickParams: textClickParams,
        };
    }

    prepareClosingMixLegend() {
        const colorObject = this._reusableFunctions.getECloseColors(true);
        this.closingMixLegend = Object.entries(colorObject).map((e) => ({
            name: e[0],
            color: e[1],
        }));
    }

    drilldownFunction() {
        let _this = this;
        let properties = _this['properties'];
        properties.router.navigate(['/reporting/production/loans-funded-by'], {
            queryParams: {
                titleSuffix: `Closing Mix: ${properties.filterValue}`,
                eCloseOptionKey: properties.eCloseOptionKey,
                navFromUrl: '/production/insights/eclose-adoption-trends',
            },
        });
    }
}

interface ColorLegendData {
    name: string;
    color: string;
}

interface AdoptionTrendsDonutChartData {
    name: string;
    color: any;
    y: number;
    properties: AdoptionTrendsDonutChartPropertiesData;
}

interface AdoptionTrendsDonutChartPropertiesData {
    units: number;
    volume: number;
    milestone: string;
    loanType: string;
    filterType: string;
    filterValue: string;
    router: Router;
    navFromUrl: string;
}

interface LegendData {
    title: string;
    description: string;
    type: string;
    textClickParams?: {
        routerUrl: string;
        queryParams: object;
    };
}

interface ClosingMixDataInfo {
    category: string;
    amount: number;
    nbr: number;
    eCloseOptionKey: number;
}
